// ADM-1 (2026-06-12) — Central de Tickets do Administrativo.
// Kanban dos pedidos abertos pela equipe (requisições de viagem/suprimentos/
// manutenção + ouvidoria anônima). A assistente e a gerente administrativa
// triam, comentam (o solicitante é notificado), atribuem e movem os cards.
// Gate: administrativo.tickets.gerenciar.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { useSearchParams } from 'react-router-dom';
import {
  App as AntApp, Button, Card, Checkbox, Drawer, Divider, Flex, Input, Select,
  Space, Spin, Tag, Typography, Empty, Segmented, Table, DatePicker, Modal,
} from 'antd';
import { ReloadOutlined, SendOutlined, AppstoreOutlined, BarsOutlined, EditOutlined, DollarOutlined, CalendarOutlined, DeleteOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { fmtDataBr, fmtMomentoBr } from '../utils/dataFmt';
import { usePerms } from '../hooks/usePerms';
import { ModalNovaDespesa } from './financeiro-contas-pagar/ModalNovaDespesa';
import { despesaDoTicket, DespesaInicial } from '../utils/despesaOpcoes';
import {
  TICKET_STATUS_CFG, TICKET_TIPO_CFG, TICKET_PRIORIDADE_CFG, DadosTicket,
  DrawerPedido, ComentarioCard, UsuarioTicket, ParticipanteTicket,
} from './AtividadesRequisicoes';
import { itemDoChamado } from '../utils/chamadoCampo';

const { Text, Title } = Typography;
const { TextArea } = Input;

const COLUNAS_KANBAN = ['novo', 'em_andamento', 'aguardando', 'concluido'];

// Nomes de mês/dia em português só onde aparecem no calendário. Um
// dayjs.locale() global mudaria os formatos de todas as outras telas.
function fmtPtBr(d: Dayjs, formato: string) {
  const s = d.locale('pt-br').format(formato);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// criado_em/atualizado_em são timestamps (UTC no JSON): o dia certo é o de
// São Paulo, igual ao que o servidor usa para ordenar. Mesma régua em
// Requisições e nos comentários (utils/dataFmt.ts).
const fmtMomento = fmtMomentoBr;

interface Ticket {
  id: string;
  protocolo: string;
  tipo: string;
  subtipo: string | null;
  titulo: string;
  descricao: string | null;
  dados: any;
  prioridade: string;
  status: string;
  anonimo: boolean;
  criado_por: string | null;
  criado_por_nome: string | null;
  contrato_numero: string | null;
  atribuido_para: string | null;
  criado_em: string;
  atualizado_em: string;
  cancelado_motivo: string | null;
  cancelado_por: string | null;
  n_comentarios: number;
  data_calendario_inicio?: string | null;
  data_calendario_fim?: string | null;
  participantes?: ParticipanteTicket[];
  participantes_versao?: number;
  sou_participante?: boolean;
  sou_dono?: boolean;
  pode_cancelar?: boolean;
}

function AdministrativoTicketsInner() {
  const { message } = AntApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState<'kanban' | 'lista' | 'calendario'>('kanban');
  const [filtroTipo, setFiltroTipo] = useState<string | null>(null);
  const [filtroColaborador, setFiltroColaborador] = useState<string | null>(null);
  const [filtroCentro, setFiltroCentro] = useState<string | null>(null);
  // Mais recente primeiro: a Central abre com o que acabou de chegar no topo
  const [ordenarData, setOrdenarData] = useState<'asc' | 'desc'>('desc');
  const [campoData, setCampoData] = useState<'criado_em' | 'data_ida'>('criado_em');
  const [modoCalendario, setModoCalendario] = useState<'semana' | 'mes'>('semana');
  const [referenciaCalendario, setReferenciaCalendario] = useState<Dayjs>(dayjs());
  const [usuarios, setUsuarios] = useState<UsuarioTicket[]>([]);
  const [centrosConhecidos, setCentrosConhecidos] = useState<string[]>([]);
  // Cancelados/excluídos não poluem o kanban; o checkbox traz na Lista
  const [verCancelados, setVerCancelados] = useState(false);
  const [aberto, setAberto] = useState<Ticket | null>(null);
  const [editando, setEditando] = useState<Ticket | null>(null);
  // Lançar o pedido no Contas a Pagar (2026-09-18) — abre o modal de despesa
  // já preenchido com o que o pedido informou; quem confirma é a pessoa.
  const { pode } = usePerms();
  const [lancando, setLancando] = useState<DespesaInicial | null>(null);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  // detalhe
  const [coments, setComents] = useState<any[]>([]);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  // O `dados` FRESCO chegou? Só com ele o botão de lançar pode aparecer — o
  // card da lista pode ter minutos de idade e não saber que já foi lançado.
  const [detalheOk, setDetalheOk] = useState(false);
  const [texto, setTexto] = useState('');
  const [mencionadosIds, setMencionadosIds] = useState<string[]>([]);
  const [interno, setInterno] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const periodoCalendario = useMemo(() => {
    const inicio = modoCalendario === 'semana' ? referenciaCalendario.startOf('week') : referenciaCalendario.startOf('month').startOf('week');
    const fim = modoCalendario === 'semana' ? referenciaCalendario.endOf('week') : referenciaCalendario.endOf('month').endOf('week');
    return { inicio, fim };
  }, [modoCalendario, referenciaCalendario]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_listar_admin', tipo: filtroTipo || undefined,
          incluir_cancelados: verCancelados || undefined,
          colaborador_id: filtroColaborador || undefined,
          centro_custo: filtroCentro || undefined,
          ordenar_data: ordenarData, data_campo: vista === 'calendario' ? 'periodo_viagem' : campoData,
          data_de: vista === 'calendario' ? periodoCalendario.inicio.format('YYYY-MM-DD') : undefined,
          data_ate: vista === 'calendario' ? periodoCalendario.fim.format('YYYY-MM-DD') : undefined },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const lista = Array.isArray(d) ? d : [];
      setTickets(lista);
      setCentrosConhecidos((anteriores) => Array.from(new Set([
        ...anteriores,
        ...lista.flatMap((t: Ticket) => Array.isArray(t.dados?.centros_custo) ? t.dados.centros_custo : []),
      ])).sort());
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message, filtroTipo, verCancelados, filtroColaborador, filtroCentro, ordenarData, campoData, vista, periodoCalendario]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action: 'adm_ticket_usuarios' }, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios(Array.isArray(d) ? d : []);
      } catch { /* filtros continuam sem a lista de pessoas */ }
    })();
  }, []);

  // Links do sino/Teams também abrem tickets fora da lista filtrada, inclusive
  // quando o gestor ainda não tem nenhum card visível.
  const ultimoDeepLink = useRef<string | null>(null);
  useEffect(() => {
    const alvo = searchParams.get('ticket') || sessionStorage.getItem('tickets_abrir_id');
    if (!alvo) { ultimoDeepLink.current = null; return; }
    if (loading || ultimoDeepLink.current === alvo) return;
    ultimoDeepLink.current = alvo;
    sessionStorage.removeItem('tickets_abrir_id');
    const t = tickets.find((x) => String(x.id) === alvo);
    if (!t) {
      // Pode ser cancelado e estar fora da lista padrão. O servidor continua
      // sendo a autoridade de acesso antes de abrir pelo link do Teams/sino.
      (async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_ticket_obter', id: alvo }, { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          setSearchParams({}, { replace: true });
          carregarDetalhe(d);
        } catch { message.warning('Este pedido não está disponível para sua conta.'); }
      })();
      return;
    }
    if (searchParams.get('ticket')) setSearchParams({}, { replace: true });
    carregarDetalhe(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, loading, searchParams, setSearchParams]);

  async function carregarDetalhe(t: Ticket) {
    setAberto(t);
    setDetalheOk(false);
    setCarregandoDetalhe(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_obter', id: t.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setComents(d.comentarios || []);
      // O card da lista pode ter minutos de idade. Sem trazer o `dados` fresco,
      // um pedido que outra pessoa acabou de lançar ainda ofereceria o botão
      // "Lançar no Contas a Pagar" — e a despesa entraria duas vezes.
      const { comentarios, sou_gestor, ...ticketFresco } = d;
      setAberto(prev => (prev && prev.id === t.id ? { ...prev, ...ticketFresco } : prev));
      setDetalheOk(true);
    } catch {
      // Falha de leitura NÃO pode virar "pode lançar": sem o dado fresco, o
      // pedido que outra pessoa acabou de lançar ainda ofereceria o botão, e a
      // despesa entraria duas vezes. Some o botão e diz por quê.
      setComents([]);
      message.warning('Não consegui reler este pedido agora — reabra antes de lançar no Contas a Pagar.', 8);
    } finally { setCarregandoDetalhe(false); }
  }

  async function mover(id: string, status: string) {
    const t = tickets.find(x => x.id === id);
    if (!t || t.status === status) return;
    const anterior = t.status;
    setTickets(ts => ts.map(x => x.id === id ? { ...x, status } : x));
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_mover', id, status },
        { headers: { Authorization: token } });
      message.success(`${t.protocolo} → ${TICKET_STATUS_CFG[status]?.label}`);
    } catch (e: any) {
      setTickets(ts => ts.map(x => x.id === id ? { ...x, status: anterior } : x));
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  async function alterar(id: string, campos: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_mover', id, ...campos },
        { headers: { Authorization: token } });
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  // Carimba o ticket depois que a despesa foi criada. Se ESTE passo falhar, o
  // lançamento já existe — por isso o aviso traz os ids: sem o carimbo o
  // pedido volta a oferecer o botão e alguém lança de novo.
  async function marcarLancado(ids: string[]) {
    if (!aberto) return;
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_marcar_lancado', id: aberto.id, lancamento_ids: ids },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAberto(prev => prev
        ? { ...prev, dados: { ...prev.dados, cp_lancamento: d.cp_lancamento } } : prev);
      carregar();
    } catch (e: any) {
      message.warning('A despesa foi criada, mas o pedido não ficou marcado como lançado '
        + `(${e.response?.data?.error || e.message}). Confira no Contas a Pagar antes de lançar de novo.`, 10);
      carregar();
    }
  }

  async function comentar() {
    if (!texto.trim() || !aberto) return;
    setEnviando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'adm_ticket_comentar', id: aberto.id, texto: texto.trim(), interno, mencionados_ids: mencionadosIds },
        { headers: { Authorization: token } });
      setTexto(''); setInterno(false); setMencionadosIds([]);
      await carregarDetalhe(aberto);
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setEnviando(false); }
  }

  function cancelarOutro(t: Ticket) {
    let justificativa = '';
    Modal.confirm({
      title: `Cancelar ${t.protocolo}`,
      content: <div><Text>Explique o motivo do cancelamento. Ele ficará no histórico do pedido.</Text>
        <Input.TextArea rows={3} style={{ marginTop: 10 }} placeholder="Justificativa obrigatória"
          onChange={(e) => { justificativa = e.target.value; }} /></div>,
      okText: 'Cancelar pedido', okButtonProps: { danger: true }, cancelText: 'Voltar',
      onOk: async () => {
        if (justificativa.trim().length < 10) {
          message.error('Informe uma justificativa com pelo menos 10 caracteres.');
          throw new Error('justificativa obrigatória');
        }
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'adm_ticket_excluir', id: t.id, justificativa: justificativa.trim() },
            { headers: { Authorization: token } });
        } catch (e: any) {
          // Relança para o modal continuar aberto com a justificativa digitada
          message.error('Falha ao cancelar: ' + (e.response?.data?.error || e.message));
          throw e;
        }
        message.success('Pedido cancelado e registrado no histórico.');
        setAberto(null); carregar();
      },
    });
  }

  function solicitante(t: Ticket) {
    return t.anonimo ? '🕵️ Anônimo' : (t.criado_por_nome || t.criado_por?.split('@')[0] || '—');
  }

  // Editar só enquanto o pedido está em aberto. Cancelar segue o servidor
  // (pode_cancelar): perfis financeiros também cancelam pedido concluído.
  const podeEditarAberto = !!aberto && aberto.tipo !== 'ouvidoria'
    && !['concluido', 'cancelado'].includes(aberto.status);
  const podeCancelarAberto = !!aberto?.pode_cancelar && aberto.status !== 'cancelado';

  const centros = centrosConhecidos;
  const inicioCalendario = periodoCalendario.inicio;
  const fimCalendario = periodoCalendario.fim;
  const diasCalendario: Dayjs[] = [];
  for (let d = inicioCalendario; d.isBefore(fimCalendario) || d.isSame(fimCalendario, 'day'); d = d.add(1, 'day')) diasCalendario.push(d);
  const calendario = (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 10 }}>
        <Segmented size="small" value={modoCalendario} onChange={(v) => setModoCalendario(v as 'semana' | 'mes')}
          options={[{ value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mês' }]} />
        <Space size={4}>
          <Button size="small" aria-label="Período anterior" icon={<LeftOutlined />}
            onClick={() => setReferenciaCalendario((v) => v.add(-1, modoCalendario === 'semana' ? 'week' : 'month'))} />
          <Text style={{ minWidth: 150, textAlign: 'center', fontSize: 12 }}>
            {modoCalendario === 'semana'
              ? `${inicioCalendario.format('DD/MM')} a ${fimCalendario.format('DD/MM/YYYY')}`
              : fmtPtBr(referenciaCalendario, 'MMMM [de] YYYY')}
          </Text>
          <Button size="small" aria-label="Próximo período" icon={<RightOutlined />}
            onClick={() => setReferenciaCalendario((v) => v.add(1, modoCalendario === 'semana' ? 'week' : 'month'))} />
        </Space>
        <DatePicker value={referenciaCalendario} onChange={(v) => v && setReferenciaCalendario(v)} />
      </Flex>
      <div style={{ overflowX: 'auto', paddingBottom: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(130px, 1fr))', gap: 6, minWidth: 910 }}>
          {diasCalendario.map((dia) => {
          const itens = tickets.filter((t) => {
            const inicio = dayjs(t.data_calendario_inicio || t.dados?.data_ida || t.dados?.data_necessaria || t.dados?.data_compra || t.criado_em);
            const fim = dayjs(t.data_calendario_fim || t.dados?.data_volta || t.dados?.data_ida || inicio);
            return (dia.isAfter(inicio, 'day') || dia.isSame(inicio, 'day')) && (dia.isBefore(fim, 'day') || dia.isSame(fim, 'day'));
          });
            return <Card key={dia.format('YYYY-MM-DD')} size="small" style={{ minHeight: 125, background: dia.isSame(dayjs(), 'day') ? '#F0F5FF' : undefined }}
            title={<Text style={{ fontSize: 11 }}>{fmtPtBr(dia, 'ddd DD')}</Text>} bodyStyle={{ padding: 6 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>{itens.map((t) =>
              <Button key={t.id} type="text" block style={{ height: 'auto', textAlign: 'left', whiteSpace: 'normal', padding: 3 }} onClick={() => carregarDetalhe(t)}>
                {TICKET_TIPO_CFG[t.tipo]?.emoji || '🎫'} {t.titulo}
              </Button>)}</Space>
            </Card>;
          })}
        </div>
      </div>
      {tickets.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum ticket neste período" />}
    </div>
  );

  const kanban = (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${COLUNAS_KANBAN.length}, minmax(240px, 1fr))`,
        gap: 12, alignItems: 'flex-start', minWidth: COLUNAS_KANBAN.length * 252,
      }}>
        {COLUNAS_KANBAN.map(st => {
          const cfg = TICKET_STATUS_CFG[st];
          const itens = tickets.filter(t => t.status === st);
          const isHover = hoverCol === st;
          return (
            <div key={st}
              onDragOver={e => { e.preventDefault(); setHoverCol(st); }}
              onDragLeave={() => setHoverCol(null)}
              onDrop={e => {
                e.preventDefault();
                if (arrastando) mover(arrastando, st);
                setArrastando(null); setHoverCol(null);
              }}>
              <Card size="small"
                style={{
                  minHeight: 280,
                  background: isHover ? '#F0F5FF' : undefined,
                  borderColor: isHover ? cfg.hex : undefined,
                  transition: 'background 0.15s, border 0.15s',
                }}
                title={
                  <Text strong style={{ color: cfg.hex, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {cfg.label} ({itens.length})
                  </Text>
                }
                styles={{ header: { borderBottom: `2px solid ${cfg.hex}`, padding: '4px 10px', minHeight: 0 } }}
                bodyStyle={{ padding: 8 }}>
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {itens.length === 0 && (
                    <Text type="secondary" italic style={{ fontSize: 10, display: 'block', textAlign: 'center', padding: '16px 4px' }}>
                      Solte aqui
                    </Text>
                  )}
                  {itens.map(t => (
                    <Card key={t.id} size="small" hoverable draggable
                      onDragStart={() => setArrastando(t.id)}
                      onClick={() => carregarDetalhe(t)}
                      style={{
                        borderLeft: `3px solid ${t.prioridade === 'urgente' ? '#f5222d' : t.prioridade === 'alta' ? '#fa8c16' : cfg.hex}`,
                        boxShadow: arrastando === t.id ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
                        opacity: arrastando === t.id ? 0.6 : 1,
                        cursor: 'grab',
                      }}
                      bodyStyle={{ padding: 8 }}>
                      <Flex justify="space-between" gap={6}>
                        <Text strong style={{ fontSize: 11, color: '#020D7F' }}>
                          {TICKET_TIPO_CFG[t.tipo]?.emoji} {t.titulo}
                        </Text>
                      </Flex>
                      {itemDoChamado(t.dados) && (
                        <Text style={{ fontSize: 10, display: 'block', marginTop: 2 }}>🚙 {itemDoChamado(t.dados)}</Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>
                        {solicitante(t)} · <Text code style={{ fontSize: 9 }}>{t.protocolo}</Text>
                      </Text>
                      <Flex justify="space-between" align="center" style={{ marginTop: 6 }}>
                        <Space size={4}>
                          {['urgente', 'alta'].includes(t.prioridade) && (
                            <Tag color={TICKET_PRIORIDADE_CFG[t.prioridade]?.cor} style={{ fontSize: 9, margin: 0 }}>
                              {TICKET_PRIORIDADE_CFG[t.prioridade]?.label}
                            </Tag>
                          )}
                          {t.atribuido_para && (
                            <Tag style={{ fontSize: 9, margin: 0 }}>{t.atribuido_para.split('@')[0]}</Tag>
                          )}
                        </Space>
                        {t.n_comentarios > 0 && (
                          <Text type="secondary" style={{ fontSize: 10 }}>💬 {t.n_comentarios}</Text>
                        )}
                      </Flex>
                    </Card>
                  ))}
                </Space>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );

  // A Lista mostra a data que está ordenando: abertura ou data da viagem.
  // Colunas secundárias somem em tela estreita para essa data caber.
  const colunas = [
    { title: 'Protocolo', dataIndex: 'protocolo', width: 95,
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: 'Tipo', dataIndex: 'tipo', width: 170, responsive: ['md'],
      filters: Object.entries(TICKET_TIPO_CFG).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (val: any, t: Ticket) => t.tipo === val,
      render: (v: string) => <Text style={{ fontSize: 12 }}>{TICKET_TIPO_CFG[v]?.emoji} {TICKET_TIPO_CFG[v]?.label || v}</Text> },
    { title: 'Pedido', dataIndex: 'titulo', ellipsis: true, width: 220,
      render: (v: string, t: Ticket) => (
        <>
          <Text strong style={{ fontSize: 12 }}>{v}</Text>
          {itemDoChamado(t.dados) && <Text type="secondary" style={{ fontSize: 11 }}> · 🚙 {itemDoChamado(t.dados)}</Text>}
        </>
      ) },
    { title: 'Solicitante', key: 's', width: 140, responsive: ['sm'],
      render: (_: any, t: Ticket) => <Text style={{ fontSize: 12 }}>{solicitante(t)}</Text> },
    campoData === 'data_ida'
      ? { title: 'Data da viagem', key: 'data_ordem', width: 110,
          render: (_: any, t: Ticket) => <Text style={{ fontSize: 11 }}>{fmtDataBr(t.dados?.data_ida)}</Text> }
      : { title: 'Abertura', key: 'data_ordem', width: 100,
          render: (_: any, t: Ticket) => <Text style={{ fontSize: 11 }}>{fmtMomento(t.criado_em)}</Text> },
    { title: 'Prioridade', dataIndex: 'prioridade', width: 95, responsive: ['lg'],
      render: (v: string) => <Tag color={TICKET_PRIORIDADE_CFG[v]?.cor}>{TICKET_PRIORIDADE_CFG[v]?.label}</Tag> },
    { title: 'Status', dataIndex: 'status', width: 125,
      render: (v: string) => <Tag color={TICKET_STATUS_CFG[v]?.cor}>{TICKET_STATUS_CFG[v]?.label}</Tag> },
    { title: 'Atualizado', dataIndex: 'atualizado_em', width: 100, responsive: ['xl'],
      render: (v: string) => <Text style={{ fontSize: 11 }}>{fmtMomento(v)}</Text> },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 14 }}>
        <Title level={4} style={{ margin: 0 }}>🎫 Central de Tickets</Title>
        <Space size={8} wrap>
          <Select allowClear placeholder="Todos os tipos" value={filtroTipo}
            onChange={setFiltroTipo} style={{ width: 200 }} size="small"
            options={Object.entries(TICKET_TIPO_CFG).map(([k, v]) => ({ value: k, label: `${v.emoji} ${v.label}` }))} />
          <Select allowClear showSearch optionFilterProp="label" placeholder="Colaborador" value={filtroColaborador}
            onChange={setFiltroColaborador} style={{ width: 190 }} size="small"
            options={usuarios.map((u) => ({ value: u.id, label: `${u.nome} (${u.email})` }))} />
          <Select allowClear showSearch optionFilterProp="label" placeholder="Centro de custo" value={filtroCentro}
            onChange={setFiltroCentro} style={{ width: 190 }} size="small"
            options={centros.map((c) => ({ value: c, label: c }))} />
          {vista !== 'calendario' && <Select value={campoData} onChange={setCampoData} size="small" style={{ width: 130 }}
            options={[{ value: 'data_ida', label: 'Data do campo' }, { value: 'criado_em', label: 'Abertura' }]} />}
          <Select value={ordenarData} onChange={setOrdenarData} size="small" style={{ width: 105 }}
            options={[{ value: 'asc', label: 'Mais cedo' }, { value: 'desc', label: 'Mais recente' }]} />
          <Segmented size="small" value={vista} onChange={(v) => setVista(v as any)}
            options={[
              { value: 'kanban', label: <Space size={4}><AppstoreOutlined />Kanban</Space> },
              { value: 'lista', label: <Space size={4}><BarsOutlined />Lista</Space> },
              { value: 'calendario', label: <Space size={4}><CalendarOutlined />Calendário</Space> },
            ]} />
          <Checkbox checked={verCancelados} onChange={e => setVerCancelados(e.target.checked)}>
            <Text style={{ fontSize: 11 }}>ver cancelados</Text>
          </Checkbox>
          <Button size="small" icon={<ReloadOutlined />} onClick={carregar} />
        </Space>
      </Flex>

      {loading ? (
        <Flex justify="center" style={{ padding: 60 }}><Spin /></Flex>
      ) : vista === 'calendario' ? calendario : tickets.length === 0 ? (
        <Empty description="Nenhum ticket aberto" />
      ) : vista === 'kanban' ? kanban : (
        <Table size="small" rowKey="id" columns={colunas as any} dataSource={tickets}
          pagination={{ pageSize: 30 }} scroll={{ x: 'max-content' }}
          onRow={(t) => ({ onClick: () => carregarDetalhe(t), style: { cursor: 'pointer' } })} />
      )}

      {/* ─── Drawer detalhe ─── */}
      <Drawer open={!!aberto} onClose={() => { setAberto(null); carregar(); }}
        title={aberto ? `${TICKET_TIPO_CFG[aberto.tipo]?.emoji} ${aberto.protocolo} — ${aberto.titulo}` : ''}
        width={600} destroyOnClose
        extra={aberto && (podeEditarAberto || podeCancelarAberto) ? (
          <Space>
            {podeEditarAberto && <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(aberto)}>Editar</Button>}
            {podeCancelarAberto && <Button size="small" danger icon={<DeleteOutlined />} onClick={() => cancelarOutro(aberto)}>Cancelar</Button>}
          </Space>
        ) : undefined}>
        {aberto && (
          <div>
            {aberto.status === 'cancelado' && (
              <Card size="small" style={{ marginBottom: 12, background: '#FFF1F0', borderColor: '#ffa39e' }}
                bodyStyle={{ padding: 10 }}>
                <Text style={{ fontSize: 12 }}>
                  🗑️ <strong>Excluído/cancelado</strong>
                  {aberto.cancelado_por && <> por {aberto.cancelado_por.split('@')[0]}</>}
                  {aberto.cancelado_motivo && <> — motivo: {aberto.cancelado_motivo}</>}
                </Text>
              </Card>
            )}
            <Flex gap={8} wrap="wrap" align="center" style={{ marginBottom: 12 }}>
              <Select size="small" value={aberto.status} style={{ width: 150 }}
                onChange={async (v) => {
                  setAberto({ ...aberto, status: v });
                  await mover(aberto.id, v);
                  // pode_cancelar depende do status: relê do servidor (concluir
                  // tira o Cancelar de quem só cancelava como dono).
                  carregarDetalhe({ ...aberto, status: v });
                }}
                options={COLUNAS_KANBAN.map(s => ({
                  value: s, label: TICKET_STATUS_CFG[s]?.label || s,
                }))} />
              <Select size="small" value={aberto.prioridade} style={{ width: 110 }}
                onChange={(v) => { setAberto({ ...aberto, prioridade: v }); alterar(aberto.id, { prioridade: v }); }}
                options={Object.entries(TICKET_PRIORIDADE_CFG).map(([k, v]) => ({ value: k, label: v.label }))} />
              <Input size="small" placeholder="Atribuir (email)" style={{ width: 200 }}
                defaultValue={aberto.atribuido_para || ''}
                onBlur={(e) => {
                  const v = e.target.value.trim() || null;
                  if (v !== aberto.atribuido_para) {
                    setAberto({ ...aberto, atribuido_para: v });
                    alterar(aberto.id, { atribuido_para: v });
                  }
                }} />
            </Flex>

            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
              Solicitante: <strong>{solicitante(aberto)}</strong>
              {aberto.anonimo && ' (ouvidoria anônima — identidade não registrada)'}
              {' · '}aberto em {fmtMomento(aberto.criado_em)}
              {aberto.subtipo && <> · {aberto.subtipo}</>}
              {aberto.contrato_numero && <> · contrato {aberto.contrato_numero}</>}
            </Text>
            {!!aberto.participantes?.length && (
              <Text type="secondary" style={{ display: 'block', fontSize: 11, marginBottom: 10 }}>
                👥 Acompanham: {aberto.participantes.map((p) => p.nome).join(', ')}
              </Text>
            )}

            <DadosTicket dados={aberto.dados} tipo={aberto.tipo} ticketId={aberto.id} />
            {aberto.tipo === 'aviso' && (
              aberto.dados?.cp_lancamento ? (
                <Card size="small" style={{ marginBottom: 12, background: '#F6FFED', borderColor: '#b7eb8f' }}
                  bodyStyle={{ padding: 10 }}>
                  <Text style={{ fontSize: 12 }}>
                    ✅ <strong>Já lançado no Contas a Pagar</strong>
                    {aberto.dados.cp_lancamento.ids?.length > 1
                      && <> — {aberto.dados.cp_lancamento.ids.length} parcelas</>}
                    {aberto.dados.cp_lancamento.por
                      && <> por {String(aberto.dados.cp_lancamento.por).split('@')[0]}</>}
                    {aberto.dados.cp_lancamento.em
                      && <> em {fmtDataBr(aberto.dados.cp_lancamento.em)}</>}
                  </Text>
                </Card>
              ) : aberto.status !== 'cancelado' && detalheOk
                   && pode('financeiro.contas_pagar.editar') && (
                <Button type="primary" size="small" icon={<DollarOutlined />}
                  style={{ marginBottom: 12 }}
                  onClick={() => setLancando(despesaDoTicket(aberto))}>
                  Lançar no Contas a Pagar
                </Button>
              )
            )}
            {aberto.descricao && (
              <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
                <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{aberto.descricao}</Text>
              </Card>
            )}

            <Divider style={{ margin: '12px 0' }} />
            <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>💬 Comunicação</Text>
            {carregandoDetalhe ? <Spin size="small" /> : (
              <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: 8 }}>
                {coments.length === 0 && (
                  <Text type="secondary" italic style={{ fontSize: 11 }}>Nenhuma mensagem ainda.</Text>
                )}
                {coments.map((c: any) => <ComentarioCard key={c.id} c={c} />)}
              </Space>
            )}
            <TextArea rows={2} value={texto} onChange={e => setTexto(e.target.value)}
              placeholder={interno ? 'Nota interna (solicitante NÃO vê)…' : 'Responder ao solicitante…'} />
            <Select mode="multiple" allowClear size="small" value={mencionadosIds} onChange={setMencionadosIds}
              optionFilterProp="label" placeholder="🔔 Avisar pessoas selecionadas (opcional)"
              style={{ width: '100%', marginTop: 6 }}
              options={usuarios.map((u) => ({ value: u.id, label: `${u.nome} (${u.email})` }))} />
            <Flex justify="space-between" align="center" style={{ marginTop: 6 }}>
              <Checkbox checked={interno} onChange={e => setInterno(e.target.checked)}>
                <Text style={{ fontSize: 11 }}>Nota interna (não visível pro solicitante)</Text>
              </Checkbox>
              <Button type="primary" size="small" icon={<SendOutlined />}
                loading={enviando} onClick={comentar} disabled={!texto.trim()}>
                Enviar
              </Button>
            </Flex>
          </div>
        )}
      </Drawer>

      {/* ─── Lançar o pedido no Contas a Pagar (2026-09-18) ─── */}
      {lancando && (
        <ModalNovaDespesa inicial={lancando}
          onFechar={() => setLancando(null)}
          onCriou={async (ids) => {
            setLancando(null);
            await marcarLancado(ids);
          }} />
      )}

      {/* ─── Drawer edição (ADM-4 — mesmo form do solicitante) ─── */}
      <DrawerPedido aberto={!!editando} ticket={editando}
        onFechar={() => setEditando(null)}
        onSalvo={async () => {
          carregar();
          // Recarrega o ticket aberto no drawer de detalhe (dados + thread)
          if (aberto) {
            try {
              const token = await getToken();
              const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
                { action: 'adm_ticket_obter', id: aberto.id },
                { headers: { Authorization: token } });
              const d = r.data.body ? JSON.parse(r.data.body) : r.data;
              setAberto(prev => prev ? { ...prev, ...d } : prev);
              setComents(d.comentarios || []);
            } catch { /* lista já recarregou */ }
          }
        }} />
    </div>
  );
}

export default function AdministrativoTickets() {
  return <AntApp><AdministrativoTicketsInner /></AntApp>;
}
